/**
 * Auth0 configuration for the LifeScience Implementations Portal
 */

import { Auth0Config } from '../Types/auth';

/**
 * Validates that required environment variables are present
 */
const validateEnvVar = (name: string, value: string | undefined): string => {
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
};

/**
 * Auth0 configuration object
 * All values are loaded from environment variables and validated
 */
export const auth0Config: Auth0Config = {
  domain: validateEnvVar('REACT_APP_AUTH0_DOMAIN', process.env.REACT_APP_AUTH0_DOMAIN),
  clientId: validateEnvVar('REACT_APP_AUTH0_CLIENTID', process.env.REACT_APP_AUTH0_CLIENTID),
  audience: validateEnvVar('REACT_APP_AUTH0_AUDIENCE', process.env.REACT_APP_AUTH0_AUDIENCE),
  redirectUri: validateEnvVar('REACT_APP_AUTH0_REDIRECTURI', process.env.REACT_APP_AUTH0_REDIRECTURI),
  returnTo: validateEnvVar('REACT_APP_AUTH0_RETURNTOURL', process.env.REACT_APP_AUTH0_RETURNTOURL)
};

/**
 * Auth0 Provider configuration options
 */
export const auth0ProviderConfig = {
  domain: auth0Config.domain,
  clientId: auth0Config.clientId,
  authorizationParams: {
    audience: auth0Config.audience,
    redirect_uri: auth0Config.redirectUri,
    scope: 'openid profile email offline_access', // Required scopes for Auth0
  },
  cacheLocation: 'localstorage' as const, // Use localStorage for refresh tokens to survive page refresh
  useRefreshTokens: true,
  useRefreshTokensFallback: false,
};

/**
 * Logout configuration
 */
export const logoutConfig = {
  clientId: auth0Config.clientId,
  logoutParams: {
    returnTo: auth0Config.returnTo
  }
};

/**
 * Login configuration with app state
 */
export const getLoginConfig = (returnTo?: string) => ({
  appState: {
    returnTo: returnTo || window.location.pathname
  }
});

/**
 * Check if Auth0 is properly configured
 */
export const isAuth0Configured = (): boolean => {
  try {
    return !!(
      auth0Config.domain &&
      auth0Config.clientId &&
      auth0Config.audience &&
      auth0Config.redirectUri &&
      auth0Config.returnTo
    );
  } catch (error) {
    console.error('Auth0 configuration error:', error);
    return false;
  }
};

/**
 * Development mode check
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};
